import { useState, useCallback, useEffect } from 'react';
import { useLazyGetPreflightStatusQuery } from '../../../api';
import { addRecoverySegment } from '../utils/preflightUtils';

/**
 * Custom hook for preflight polling logic
 * @param {Object} options - Configuration options
 * @param {Function} options.onStatusUpdate - Callback when status updates
 * @param {Function} options.onComplete - Callback when all preflights complete
 * @param {boolean} options.inspectSourcesMode - Whether in source inspection mode
 * @param {Function} options.processAutoRecovery - Auto-recovery processing function
 */
export const usePreflightPolling = ({ onStatusUpdate, onComplete, inspectSourcesMode, processAutoRecovery }) => {
    const [pollingIntervals, setPollingIntervals] = useState(new Map());
    const [getPreflightStatus] = useLazyGetPreflightStatusQuery();

    // Start polling for a specific preflight
    const startPreflightPolling = useCallback(
        (preflight) => {
            const pollPreflight = async () => {
                try {
                    const result = await getPreflightStatus(preflight.id).unwrap();

                    if (result) {
                        const state = result.state || 'pending';
                        const errorType = result.error_type;

                        // Determine progress and display state based on API response
                        let progress = 0;
                        let displayState = state;
                        let displayErrorType = errorType;

                        if (state === 'success') {
                            progress = 100;
                            displayState = 'success';
                            displayErrorType = errorType; // Keep original error_type (null for clean success, 'warning' for warnings)
                        } else if (state === 'error') {
                            progress = 100;
                            displayState = 'error';
                            displayErrorType = errorType;
                        } else if (state === 'running') {
                            progress = 50;
                            displayState = 'running';
                            displayErrorType = null;
                        } else {
                            progress = 0;
                            displayState = 'pending';
                            displayErrorType = null;
                        }

                        // Process the result data with recovery segments
                        const processedData = result
                            ? {
                                  ...result,
                                  content: result.content?.map((check) => addRecoverySegment(check)),
                              }
                            : null;

                        // Notify parent component of status update
                        if (onStatusUpdate) {
                            onStatusUpdate(preflight.id, {
                                state: displayState,
                                errorType: displayErrorType,
                                data: processedData,
                                progress,
                            });
                        }

                        // Auto-expand components with errors/warnings in inspect sources mode
                        if (inspectSourcesMode && result && result.content) {
                            const hasErrorsOrWarnings = result.content.some(
                                (check) =>
                                    check.state === 'error' ||
                                    (check.state === 'success' && check.error_type === 'warning'),
                            );
                            if (hasErrorsOrWarnings && onStatusUpdate) {
                                onStatusUpdate(preflight.id, {
                                    state: displayState,
                                    errorType: displayErrorType,
                                    data: processedData,
                                    progress,
                                    autoExpand: true,
                                });
                            }
                        }

                        // Trigger auto-recovery if component is complete and has recoverable errors
                        if ((state === 'success' || state === 'error') && result && processAutoRecovery) {
                            setTimeout(() => processAutoRecovery(preflight.id, processedData), 100);
                        }

                        // If this preflight is done, stop polling
                        if (state === 'success' || state === 'error') {
                            setPollingIntervals((prev) => {
                                const currentInterval = prev.get(preflight.id);
                                if (currentInterval) {
                                    clearInterval(currentInterval);
                                    const newMap = new Map(prev);
                                    newMap.delete(preflight.id);
                                    return newMap;
                                }
                                return prev;
                            });
                        }
                    }
                } catch (err) {
                    console.error('Error polling preflight:', err);
                }
            };

            // Start polling every 3 seconds
            const interval = setInterval(pollPreflight, 3000);
            setPollingIntervals((prev) => new Map(prev).set(preflight.id, interval));

            // Initial poll
            pollPreflight();
        },
        [getPreflightStatus, onStatusUpdate, inspectSourcesMode, processAutoRecovery],
    );

    // Stop all polling
    const stopAllPolling = useCallback(() => {
        setPollingIntervals((prev) => {
            prev.forEach((interval) => clearInterval(interval));
            return new Map();
        });
    }, []);

    // Cleanup effect
    useEffect(() => {
        return () => {
            stopAllPolling();
        };
    }, [stopAllPolling]);

    return {
        startPreflightPolling,
        stopAllPolling,
        pollingCount: pollingIntervals.size,
    };
};
